import { type CredentialResponse, GoogleLogin } from '@react-oauth/google';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { PatternFormat } from 'react-number-format';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SystemNotification } from '@/app/components/feedback/SystemNotification';
import * as Yup from 'yup';
import { toAbsoluteUrl } from '../../../../_metronic/helpers';
import { type PageLink } from '../../../../_metronic/layout/core';
import PageShell from '@/app/components/layout/PageShell';
import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import { DASHBOARD_PAGE_URL, PREFERENCES_PAGE_URL } from '../../../../api/core/links';
import { AccountEndpoints } from '../../../../api/endpoints/AccountEndpoints';
import formatDuration from '../../../helpers/formatDuration';
import { type AttachmentDto } from '../../../models/AttachmentDto';
import { type DataTableColumnDef } from '../../../models/DataTableColumnDef';
import { type EnumType } from '../../../models/EnumType';
import { type UserLoginSessionDto } from '../../../models/UserLoginSessionDto';
import { useAuth } from '../../../modules/auth/core/Auth';
import { DataTable } from '../../../modules/table/DataTable';
import StatusBadge from '@/app/modules/table/components/StatusBadge';
import DataTableBody from '../../../modules/table/components/DataTableBody';
import DataTableControls from '../../../modules/table/components/DataTableControls';
import SafeImage from '../../../modules/widgets/SafeImage';
import UploaderModal from '../../../modules/widgets/Uploader/UploaderModal';
import {
  getNotificationsAvailableList,
  getNotificationsDisabledList,
  getUserPreferencesDetails,
  linkGoogleAccount,
  saveDisabledNotifications,
  saveUserPreferences,
  unlinkGoogleAccount,
} from './core/_requests';
import TimeFilterDropdown from './components/UserSessionTimeFilterDropdown';
import EditPasswordModal from './modal/EditPasswordModal';
import EditUserModal from './modal/EditUserModal';
import { type AccountDetailsDto, initialAccountDetailsEmpty } from '../../../models/AccountDetailsDto';
import PermissionGroupToggle from '../user-role/components/PermissionGroupToggle';
import MetronicCard from '../../../components/layout/MetronicCard';

dayjs.extend(utc);
dayjs.extend(timezone);

const GOOGLE_CLIENT_ID = import.meta.env.VITE_APP_GOOGLE_CLIENT_ID as string;

const breadcrumbs: Array<PageLink> = [
  {
    title: 'Dashboard',
    path: DASHBOARD_PAGE_URL,
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
  {
    title: 'Preferências',
    path: PREFERENCES_PAGE_URL,
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
];

const formSchema = Yup.object().shape({
  name: Yup.string().required('Nome é obrigatório'),
  telephone: Yup.string().required('Telefone é obrigatório'),
});

const EDIT_USER_PREFERENCES_KEY = 'editUserPreferencesPage';
const DISABLED_NOTIFICATIONS_KEY = 'getNotificationsDisabled';

const UserPreferencesPage = () => {
  const { setCurrentUser } = useAuth();
  const queryClient = useQueryClient();
  const [isUploadImageModalVisible, setIsUploadImageModalVisible] = useState(false);
  const [isEditUserModalVisible, setIsEditUserModalVisible] = useState(false);
  const [isEditPasswordModalVisible, setIsEditPasswordModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'notifications' | 'sessions'>('general');

  const initialAfter = dayjs().subtract(1, 'hour').format('YYYY-MM-DDTHH:mm:ss');

  const formik = useFormik<AccountDetailsDto>({
    initialValues: initialAccountDetailsEmpty,
    validationSchema: formSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      editMutation.mutate(values);
    },
  });

  const { data: accountDetailsData, isPending: isLoadingAccountDetails } = useQuery({
    queryKey: [EDIT_USER_PREFERENCES_KEY],
    queryFn: async () => {
      const response = await getUserPreferencesDetails();
      return response.data;
    },
  });

  const { data: notificationsAvailable = [], isPending: isLoadingNotificationsAvailable } = useQuery({
    queryKey: ['getNotificationsAvailable'],

    queryFn: async () => {
      const response = await getNotificationsAvailableList();
      return response.data;
    },
  });

  const { data: disabledNotifications = [], isPending: isLoadingDisabledNotifications } = useQuery({
    queryKey: [DISABLED_NOTIFICATIONS_KEY],
    queryFn: async () => {
      const response = await getNotificationsDisabledList();
      return response.data;
    },
  });

  useEffect(() => {
    if (accountDetailsData) {
      formik.setValues({ ...initialAccountDetailsEmpty, ...accountDetailsData });
    }
  }, [accountDetailsData]);

  const editMutation = useMutation({
    mutationFn: async (updatedUserPreferences: AccountDetailsDto) => {
      const response = await saveUserPreferences(updatedUserPreferences);
      return response.data;
    },

    onSuccess: (data) => {
      SystemNotification.success('Configurações de conta alterada com sucesso');
      setCurrentUser(data);
      queryClient.invalidateQueries({
        queryKey: [EDIT_USER_PREFERENCES_KEY],
      });
    },

    onError: (error) => {
      SystemNotification.error(getErrorMessage(error));
    },
  });

  const disabledNotificationsMutation = useMutation({
    mutationKey: [DISABLED_NOTIFICATIONS_KEY],
    // Cada save manda a lista inteira: um por vez, na ordem dos cliques.
    scope: { id: DISABLED_NOTIFICATIONS_KEY },
    mutationFn: async (disabled: EnumType[]) => {
      const response = await saveDisabledNotifications(disabled);
      return response.data;
    },

    onMutate: (disabled) => {
      queryClient.setQueryData([DISABLED_NOTIFICATIONS_KEY], disabled);
    },

    onError: (error) => {
      SystemNotification.error(getErrorMessage(error));
    },

    // O último save da fila relê a lista do backend, que desfaz o que foi recusado.
    onSettled: async () => {
      if (queryClient.isMutating({ mutationKey: [DISABLED_NOTIFICATIONS_KEY] }) > 1) return;
      await queryClient.invalidateQueries({ queryKey: [DISABLED_NOTIFICATIONS_KEY] });
    },
  });

  const handleToggleNotification = (notification: EnumType, receive: boolean) => {
    // Do cache, e não do render: o onMutate do clique anterior pode ainda não ter chegado à tela.
    const disabled = queryClient.getQueryData<EnumType[]>([DISABLED_NOTIFICATIONS_KEY]) ?? [];
    const others = disabled.filter((item) => item.value !== notification.value);
    disabledNotificationsMutation.mutate(receive ? others : [...others, notification]);
  };

  const handleGoogleLoginSuccess = ({ credential }: CredentialResponse) => {
    if (!credential) {
      SystemNotification.error('Erro ao conectar com o Google: Credenciais inválidas');
      return;
    }
    sendLinkGoogleAccountRequest(credential);
  };

  const handleGoogleLoginError = () => {
    SystemNotification.error('Erro ao conectar com o Google');
  };

  const { mutate: sendLinkGoogleAccountRequest } = useMutation({
    mutationFn: async (token: string) => {
      const response = await linkGoogleAccount(token);
      return response.data;
    },

    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: [EDIT_USER_PREFERENCES_KEY],
      });
      SystemNotification.success('Conta do Google conectada com sucesso');
    },

    onError: (error) => {
      SystemNotification.error(getErrorMessage(error));
    },
  });

  const { mutate: sendUnLinkGoogleAccountRequest } = useMutation({
    mutationFn: async () => {
      const response = await unlinkGoogleAccount();
      return response.data;
    },

    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: [EDIT_USER_PREFERENCES_KEY],
      });
      SystemNotification.success('Conta do Google desconectada com sucesso');
    },

    onError: (error) => {
      SystemNotification.error(getErrorMessage(error));
    },
  });

  // IMAGE UPLOADER
  const onUploadImageModalClose = (attachments?: AttachmentDto[]) => {
    if (attachments && attachments.length > 0) {
      const newImage = attachments[0];
      formik.setFieldValue('profile_picture_url', newImage.public_url || '');
      formik.setFieldValue('profile_picture_id', newImage.id);
    }
    setIsUploadImageModalVisible(false);
  };

  const handleOnShowModalClick = () => {
    setIsUploadImageModalVisible(true);
  };

  const handleOnClearImageClick = () => {
    formik.setFieldValue('profile_picture_url', '');
    formik.setFieldValue('profile_picture_id', '');
  };

  const isGoogleConnected: boolean = useMemo(() => {
    if (!formik.values.google_account) return false;
    return formik.values.google_account;
  }, [formik.values.google_account]);

  const columns: DataTableColumnDef<UserLoginSessionDto>[] = [
    {
      id: 'location',
      header: 'Localização',
      accessorKey: 'location',

      cell: ({ row }) => {
        return <div className="text-gray-600 fw-bold">{row.getValue('location') || '--'}</div>;
      },
    },
    {
      id: 'active',
      header: 'Situação',
      accessorKey: 'active',
      enableSorting: false,
      filterFn: 'equals',

      cell: ({ row }) => {
        if (!row) return null;
        const active = row.getValue('active');
        return active === true ? (
          <StatusBadge tone="success" label="Ativo" />
        ) : (
          <StatusBadge tone="warning" label="Expirado" />
        );
      },
    },
    {
      id: 'device',
      header: 'Dispositivo',
      accessorKey: 'device',

      cell: ({ row }) => {
        return <div className="text-gray-600 fw-bold">{row.getValue('device') || '--'}</div>;
      },
    },
    {
      id: 'ip_address',
      header: 'IP',
      accessorKey: 'ip_address',

      cell: ({ row }) => {
        return <div className="text-gray-600 fw-bold">{row.getValue('ip_address') || '--'}</div>;
      },
    },
    {
      id: 'creation_date',
      header: 'Quando',
      accessorKey: 'creation_date',

      cell: ({ row }) => {
        const raw = row.getValue<string>('creation_date');
        if (!raw) return <div className="text-gray-600 fw-bold">--</div>;

        const seconds = dayjs().diff(dayjs(raw), 'second');
        const texto = formatDuration(seconds);

        return <div className="text-gray-600 fw-bold">{texto}</div>;
      },
    },
  ];

  return (
    <PageShell breadcrumbs={breadcrumbs} title="Configuração de conta">
      <UploaderModal
        attachments={[]}
        maxQuantity={1}
        minQuantity={1}
        show={isUploadImageModalVisible}
        onClose={onUploadImageModalClose}
      />
      <EditUserModal
        email={formik.values.email || ''}
        show={isEditUserModalVisible}
        onClose={() => {
          setIsEditUserModalVisible(false);
        }}
      />
      <EditPasswordModal show={isEditPasswordModalVisible} onClose={() => setIsEditPasswordModalVisible(false)} />

      <div className="d-flex flex-column flex-lg-row gap-5 gap-lg-9 mt-5">
        <div className="d-flex flex-column gap-5 gap-lg-9 w-100">
          {/* Tabs Navigation */}
          <ul className="nav nav-tabs nav-line-tabs nav-line-tabs-2x mb-5 fs-6">
            <li className="nav-item">
              <a
                className={`nav-link ${activeTab === 'general' ? 'active' : ''}`}
                data-bs-toggle="tab"
                href="#general"
                onClick={() => setActiveTab('general')}
              >
                Geral
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${activeTab === 'notifications' ? 'active' : ''}`}
                data-bs-toggle="tab"
                href="#notifications"
                onClick={() => setActiveTab('notifications')}
              >
                Notificações
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${activeTab === 'sessions' ? 'active' : ''}`}
                data-bs-toggle="tab"
                href="#sessions"
                onClick={() => setActiveTab('sessions')}
              >
                Histórico de Sessões
              </a>
            </li>
          </ul>

          {/* Tabs Content */}
          <div className="tab-content" id="myTabContent">
            {/* General Tab */}
            <div
              className={`tab-pane fade ${activeTab === 'general' ? 'show active' : ''}`}
              id="general"
              role="tabpanel"
            >
              <div className="d-flex flex-column gap-5">
                {/* Details Form */}
                <MetronicCard loading={isLoadingAccountDetails}>
                  <form onSubmit={formik.handleSubmit}>
                    <div className="card-header">
                      <h3 className="card-title fs-3 text-gray-800">Detalhes</h3>
                    </div>
                    <div className="card-body p-3 p-lg-9 d-flex flex-column gap-8">
                      <div className="row">
                        <label className="form-label col-4 fs-5 mb-4 text-gray-800">Foto</label>
                        <div className="col-8">
                          <div>
                            <div className="position-relative d-inline-block">
                              <SafeImage
                                alt="Imagem da Conta de Usuário"
                                className="mb-4 align-self-center rounded cursor-pointer"
                                height={100}
                                src={formik.values.profile_picture_url || '/media/avatars/AddProductImage.svg'}
                                width={100}
                                onClick={handleOnShowModalClick}
                              />
                              <div
                                className="position-absolute top-0 end-0 d-flex justify-content-center align-items-center bg-secondary rounded-circle cursor-pointer"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  transform: 'translate(25%, -25%)',
                                }}
                                onClick={handleOnShowModalClick}
                              >
                                <i className="bi bi-pencil" />
                              </div>
                              {!!formik.values.profile_picture_id && (
                                <div
                                  className="position-absolute end-0 d-flex justify-content-center align-items-center bg-secondary rounded-circle cursor-pointer"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    transform: 'translate(25%, -25%)',
                                    top: '35px',
                                  }}
                                  onClick={handleOnClearImageClick}
                                >
                                  <i className="bi bi-trash3" />
                                </div>
                              )}
                            </div>
                          </div>
                          <p className="text-gray-600 fs-8 mb-0">
                            Somente arquivos de imagem *.png, *.jpg e *.jpeg são aceitos
                          </p>
                        </div>
                      </div>
                      <div className="row align-items-center">
                        <label className="form-label col-4 fs-5 mb-0 text-gray-800 required">Nome</label>
                        <div className="col-8">
                          <input
                            className="form-control"
                            id="name"
                            placeholder="Nome"
                            type="text"
                            {...formik.getFieldProps('name')}
                          />
                          {!!formik.touched.name && !!formik.errors.name && (
                            <div className="fv-plugins-message-container">
                              <div className="fv-help-block">{formik.errors.name}</div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="row align-items-center">
                        <label className="form-label col-4 fs-5 mb-0 text-gray-800">Cargo</label>
                        <div className="col-8">
                          <input
                            className="form-control"
                            id="role"
                            placeholder="Cargo"
                            type="text"
                            {...formik.getFieldProps('role')}
                          />
                        </div>
                      </div>
                      <div className="row align-items-center">
                        <label className="form-label col-4 fs-5 mb-0 text-gray-800 required">Telefone</label>
                        <div className="col-8">
                          <PatternFormat
                            className="form-control"
                            format="(##) #####-####"
                            id="telephone"
                            placeholder="(xx) xxxxx-xxxx"
                            {...formik.getFieldProps('telephone')}
                          />
                          {!!formik.touched.telephone && !!formik.errors.telephone && (
                            <div className="fv-plugins-message-container">
                              <div className="fv-help-block">{formik.errors.telephone}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="card-footer">
                      <div className="d-flex flex-end gap-4">
                        <button className="btn btn-primary" type="submit">
                          Salvar
                        </button>
                      </div>
                    </div>
                  </form>
                </MetronicCard>

                {/* Method of Entry */}
                <MetronicCard loading={isLoadingAccountDetails}>
                  <div className="card-header">
                    <h3 className="card-title fs-3 text-gray-800">Método de entrada</h3>
                  </div>
                  <div className="card-body p-3 p-lg-9 d-flex flex-column gap-8">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex flex-column">
                        <label className="form-label fs-5 text-gray-800">Usuário</label>
                        <span className="text-gray-600 fw-bold">{formik.values.email}</span>
                      </div>
                      <button
                        className="btn btn-secondary btn-sm text-gray-700"
                        type="button"
                        onClick={() => setIsEditUserModalVisible(true)}
                      >
                        Mudar usuário
                      </button>
                    </div>
                    <div className="separator separator-dashed" />
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex flex-column">
                        <label className="form-label fs-5 text-gray-800">Senha</label>
                        <span className="text-gray-600 fw-bold">**********</span>
                      </div>
                      <button
                        className="btn btn-secondary btn-sm text-gray-700"
                        type="button"
                        onClick={() => setIsEditPasswordModalVisible(true)}
                      >
                        Mudar senha
                      </button>
                    </div>
                    <div className="alert alert-primary d-flex align-items-center p-5 border-dashed border-primary m-0 justify-content-between">
                      <div className="d-flex align-items-center">
                        <i className="ki-solid ki-shield-cross fs-2hx text-primary me-4">
                          <span className="path2" />
                        </i>
                        <div className="d-flex flex-column">
                          <h4 className="mb-1 text-gray-800 fs-5">Proteja sua conta</h4>
                          <span className="text-gray-600">
                            A autenticação em duas etapas adiciona uma camada extra de segurança à sua conta. Para fazer
                            login, além disso, você precisará fornecer um código de 6 dígitos.
                          </span>
                        </div>
                      </div>
                      <button className="btn btn-primary btn-sm" type="button">
                        Ativar
                      </button>
                    </div>
                  </div>
                </MetronicCard>

                {/* Connected Accounts */}
                <MetronicCard loading={isLoadingAccountDetails}>
                  <div
                    aria-controls="kt_account_connected_accounts"
                    aria-expanded="true"
                    className="card-header border-0 cursor-pointer"
                    data-bs-target="#kt_account_connected_accounts"
                    data-bs-toggle="collapse"
                    role="button"
                  >
                    <div className="card-title m-0">
                      <h3 className="fw-bold m-0">Contas conectadas</h3>
                    </div>
                  </div>
                  <div className="collapse show" id="kt_account_settings_connected_accounts">
                    <div className="card-body border-top p-9">
                      <div className="py-2">
                        <div className="d-flex flex-stack">
                          <div className="d-flex">
                            <img
                              alt=""
                              className="w-30px me-6"
                              src={toAbsoluteUrl('media/svg/brand-logos/google-icon.svg')}
                            />
                            <div className="d-flex flex-column">
                              <a className="fs-5 text-gray-900 text-hover-primary fw-bold" href="#">
                                Google
                              </a>
                              <div className="fs-6 fw-semibold text-gray-500">
                                Utilize sua conta do google para acessar rapidamente o sistema
                              </div>
                            </div>
                          </div>
                          <div className="d-flex justify-content-end">
                            {isGoogleConnected ? (
                              <button
                                className="btn btn-sm btn-danger"
                                type="button"
                                onClick={() => sendUnLinkGoogleAccountRequest()}
                              >
                                Desconectar
                              </button>
                            ) : (
                              GOOGLE_CLIENT_ID && (
                                <GoogleLogin
                                  locale="pt-BR"
                                  size="medium"
                                  text="continue_with"
                                  onError={handleGoogleLoginError}
                                  onSuccess={handleGoogleLoginSuccess}
                                />
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </MetronicCard>
              </div>
            </div>

            {/* Notifications Tab */}
            <div
              className={`tab-pane fade ${activeTab === 'notifications' ? 'show active' : ''}`}
              id="notifications"
              role="tabpanel"
            >
              <MetronicCard loading={isLoadingNotificationsAvailable || isLoadingDisabledNotifications}>
                <div className="card-header">
                  <h3 className="card-title fs-3 text-gray-800">Notificações</h3>
                </div>
                <div className="card-body p-3 p-lg-9 d-flex flex-column gap-8">
                  <p className="text-gray-600 fs-6 mb-0">
                    Desligue os tipos que não quer receber no sino. As notificações urgentes chegam sempre.
                  </p>
                  <ul className="list-unstyled">
                    {notificationsAvailable.map((n) => (
                      <li key={n.value}>
                        <PermissionGroupToggle
                          checked={!disabledNotifications.some((item) => item.value === n.value)}
                          helperText={n.description}
                          title={n.extra}
                          onChange={(e) => handleToggleNotification(n, e.target.checked)}
                        />
                        {notificationsAvailable.length - 1 !== notificationsAvailable.indexOf(n) && (
                          <div className="separator separator-dashed my-6" />
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </MetronicCard>
            </div>

            {/* Sessions Tab */}
            <div
              className={`tab-pane fade ${activeTab === 'sessions' ? 'show active' : ''}`}
              id="sessions"
              role="tabpanel"
            >
              <MetronicCard className="h-auto h-500px">
                <DataTable
                  columns={columns}
                  endpoint={AccountEndpoints.loginSessionList}
                  variant="list"
                  exportEndpoint={AccountEndpoints.loginSessionExport}
                  exportFilterLabels={{}}
                  filter={{ after: initialAfter }}
                  hasMinHeight={true}
                  isExportable={true}
                  isRefreshable={false}
                  isSearchable={false}
                  tableKey="user-preferences-sessions"
                >
                  <div className="card-header d-flex align-items-center">
                    <h3 className="card-title fs-3 text-gray-800">Histórico de sessões</h3>
                    <TimeFilterDropdown defaultPeriod="1h" />
                  </div>
                  <div className="card-body py-0">
                    <DataTableControls />
                    <DataTableBody showHeader={true} />
                  </div>
                </DataTable>
              </MetronicCard>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default UserPreferencesPage;
